// B2B / Firmy (schválená varianta A): fronta Dnes, filtry, tabulka (počítač) nebo seznam (telefon), kanban.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Building2, Plus, RefreshCw, BellRing, Search, List, LayoutGrid, Layers, Phone, Mail } from "lucide-react";
import GradientHeader from "../../components/GradientHeader.jsx";
import { Stranka, Segment, KartaSeznam, PruhFiltru, RozbalovaciVyber, VyberSeznamu, ErrorNote, SkeletonRows, EmptyState } from "../../components/ui.jsx";
import { Pil, RadekKontaktu, KartaObal, PruhSkupiny } from "../../components/sales/salesUi.jsx";
import { useUzkaObrazovka } from "../../lib/obrazovka.js";
import { b2bGet } from "../../components/b2b/b2bApi.js";
import { StavPil, SegPil, OtevritTlacitko, ZDROJE, krokPil, volbyStavu, volbySegmentu, dmr, telHref } from "../../components/b2b/b2bUi.jsx";
import { FirmaDialog } from "../../components/b2b/FirmaDialogy.jsx";

const REZIMY = [
  { key: "tabulka", label: <span className="inline-flex items-center gap-1.5"><List className="h-4 w-4" />Tabulka</span> },
  { key: "kanban", label: <span className="inline-flex items-center gap-1.5"><LayoutGrid className="h-4 w-4" />Kanban</span> },
];

/* --- Fronta Dnes -------------------------------------------------------------- */
const TLACITKO = { email: "Napsat", odpovedet: "Odpovědět", followup: "Follow-up", zavolat: "Zavolat", otevrit: null };

function RadekFronty({ p, uzka, prvni }) {
  const popis = TLACITKO[p.akce];
  let hlavni = null;
  if (p.akce === "zavolat" && p.telefon) hlavni = { href: telHref(p.telefon), Ikona: Phone, text: "Zavolat" };
  else if (["email", "odpovedet", "followup"].includes(p.akce) && p.email) hlavni = { href: `mailto:${p.email}`, Ikona: Mail, text: popis };
  const Ikona = hlavni?.Ikona;
  return (
    <div className={`bg-white px-4 py-3 ${prvni ? "" : "border-t border-slate-100"}`}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-base font-bold leading-snug text-slate-900">{p.nazev}</div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            <SegPil segment={p.segment} />
            {p.info.length > 0 && <Pil ton="neutralni" casti={p.info} />}
          </div>
          {(p.poznamka || p.osoba) && <div className="mt-1.5 text-[13px] leading-snug text-slate-600">{[p.poznamka, p.osoba].filter(Boolean).join(", ")}</div>}
        </div>
        {!uzka && (
          <div className="flex shrink-0 items-center gap-2">
            {hlavni && <a href={hlavni.href} className="btn-secondary btn-sm whitespace-nowrap"><Ikona className="h-4 w-4" />{hlavni.text}</a>}
            <OtevritTlacitko id={p.firma_id} nazev={p.nazev} />
          </div>
        )}
      </div>
      {uzka && (
        <div className="mt-3 flex items-center gap-2">
          {hlavni && <a href={hlavni.href} className="btn-secondary flex-1"><Ikona className="h-4 w-4" />{hlavni.text}</a>}
          {!hlavni && <span className="flex-1" />}
          <OtevritTlacitko id={p.firma_id} nazev={p.nazev} />
        </div>
      )}
    </div>
  );
}

function FrontaDnes({ data, uzka }) {
  if (!data || data.celkem === 0) return null;
  return (
    <KartaSeznam ikona={BellRing} title="Dnes na řadě" podtitulek={dmr(data.dnes)} odznak={data.celkem} odznakTon="zluta">
      {data.skupiny.map((g) => (
        <div key={g.klic}>
          <PruhSkupiny title={g.nazev} pocet={g.polozky.length} ton={g.ton} />
          {g.polozky.map((p, i) => <RadekFronty key={p.firma_id} p={p} uzka={uzka} prvni={i === 0} />)}
        </div>
      ))}
    </KartaSeznam>
  );
}

/* --- Seznamy ------------------------------------------------------------------ */
function DalsiKrok({ f }) {
  const k = krokPil(f);
  if (!f.dalsi_krok && !k) return <span className="text-sm text-slate-400">bez kroku</span>;
  return (
    <div className="space-y-1">
      {f.dalsi_krok && <div className="text-sm text-slate-800">{f.dalsi_krok}</div>}
      {k && <Pil ton={k.ton} mala casti={k.text} />}
    </div>
  );
}

function TabulkaFirem({ firmy, obchodnici }) {
  const navigate = useNavigate();
  const jmeno = (e) => obchodnici.find((o) => o.email === e)?.jmeno || e || "bez obchodníka";
  return (
    <KartaObal>
      <div className="p-3 sm:p-4">
        <div className="hidden overflow-x-auto sm:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2">Firma</th><th className="px-3 py-2">Segment</th><th className="px-3 py-2">Stav</th>
                <th className="px-3 py-2">Kontakt</th><th className="px-3 py-2">Další krok</th><th className="px-3 py-2">Obchodník</th>
                <th className="px-3 py-2">Zdroj</th><th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {firmy.map((f) => (
                <tr key={f.id} className="cursor-pointer border-t border-slate-100 align-top" onClick={() => navigate(`/b2b/firma/${f.id}`)}>
                  <td className="px-3 py-2.5"><div className="font-bold text-brand-deep">{f.nazev}</div><div className="text-xs text-slate-500">{[f.mesto, f.velikost ? `${f.velikost} zaměstnanců` : ""].filter(Boolean).join(", ")}</div></td>
                  <td className="px-3 py-2.5"><SegPil segment={f.segment} mala /></td>
                  <td className="px-3 py-2.5"><StavPil stav={f.stav} mala /></td>
                  <td className="px-3 py-2.5 text-slate-700">{f.osoba || "bez kontaktu"}<div className="text-xs text-slate-500">{f.osoba_role}</div></td>
                  <td className="px-3 py-2.5"><DalsiKrok f={f} /></td>
                  <td className="px-3 py-2.5 text-slate-700">{jmeno(f.obchodnik_id)}</td>
                  <td className="px-3 py-2.5 text-slate-600">{ZDROJE[f.zdroj] || f.zdroj}</td>
                  <td className="px-3 py-2.5 text-right" onClick={(e) => e.stopPropagation()}><OtevritTlacitko id={f.id} nazev={f.nazev} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </KartaObal>
  );
}

function pilulkyFirmy(f) {
  const k = krokPil(f);
  return <><StavPil stav={f.stav} /><SegPil segment={f.segment} />{k && <Pil ton={k.ton} casti={["další krok", k.text]} />}</>;
}
function SeznamFirem({ firmy }) {
  const navigate = useNavigate();
  return (
    <KartaObal>
      {firmy.map((f, i) => (
        <RadekKontaktu key={f.id} prvni={i === 0} jmeno={f.nazev} onOtevrit={() => navigate(`/b2b/firma/${f.id}`)} pilulky={pilulkyFirmy(f)} poznamka={f.dalsi_krok} />
      ))}
    </KartaObal>
  );
}

/* --- Kanban ------------------------------------------------------------------- */
const SLOUPCE = [
  { klic: "k", nazev: "K oslovení", stavy: ["k_osloveni"], ton: "neutralni" },
  { klic: "o", nazev: "Osloveno", stavy: ["osloveno", "followup"], ton: "info" },
  { klic: "z", nazev: "Zájem", stavy: ["zajem"], ton: "ok" },
  { klic: "s", nazev: "Schůzka", stavy: ["schuzka"], ton: "ok" },
  { klic: "n", nazev: "Nabídka", stavy: ["nabidka"], ton: "znacka" },
  { klic: "c", nazev: "Klient", stavy: ["klient"], ton: "zlata" },
];
const MIMO = { klic: "m", nazev: "Mimo hru", stavy: ["odlozeno", "ztraceno", "nedoruceno", "nekontaktovat"], ton: "neutralni" };

function KartaKanbanu({ f }) {
  const navigate = useNavigate();
  const k = krokPil(f);
  return (
    <button type="button" onClick={() => navigate(`/b2b/firma/${f.id}`)} className="polozka block w-full space-y-1.5 text-left active:bg-brand-50">
      <span className="block text-sm font-bold leading-snug text-slate-900">{f.nazev}</span>
      <span className="flex flex-wrap gap-1">
        {f.stav === "followup" && <StavPil stav="followup" mala />}
        <SegPil segment={f.segment} mala />
      </span>
      {f.osoba && <span className="block text-xs text-slate-600">{f.osoba}</span>}
      {k && <span className="block"><Pil ton={k.ton} mala casti={k.text} /></span>}
    </button>
  );
}

function Kanban({ firmy, uzka }) {
  const navigate = useNavigate();
  const [vyber, setVyber] = useState(false);
  const [sloupec, setSloupec] = useState("o");
  const vSloupci = (s) => firmy.filter((f) => s.stavy.includes(f.stav));
  const mimoRadky = (arr) => arr.map((f, i) => (
    <RadekKontaktu key={f.id} prvni={i === 0} jmeno={f.nazev} poznamka={f.dalsi_krok} pilulky={pilulkyFirmy(f)} onOtevrit={() => navigate(`/b2b/firma/${f.id}`)} />
  ));
  if (!uzka) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-6 items-start gap-3">
          {SLOUPCE.map((s) => {
            const f = vSloupci(s);
            return (
              <KartaObal key={s.klic}>
                <PruhSkupiny title={s.nazev} pocet={f.length} ton={s.ton} />
                <div className="space-y-2 p-2">{f.map((x) => <KartaKanbanu key={x.id} f={x} />)}</div>
              </KartaObal>
            );
          })}
        </div>
        <KartaSeznam ikona={Layers} title="Mimo hru" odznak={vSloupci(MIMO).length}>{mimoRadky(vSloupci(MIMO))}</KartaSeznam>
      </div>
    );
  }
  const vsechny = [...SLOUPCE, MIMO];
  const s = vsechny.find((x) => x.klic === sloupec);
  const f = vSloupci(s);
  return (
    <>
      <KartaSeznam ikona={Layers} title={s.nazev} podtitulek="klepni pro změnu sloupce" odznak={f.length} sbalitelne otevreno onToggle={() => setVyber(true)}>
        {mimoRadky(f)}
      </KartaSeznam>
      <VyberSeznamu open={vyber} onClose={() => setVyber(false)} title="Který sloupec" ikona={Layers} value={sloupec} onChange={setSloupec}
        skupiny={[
          { nadpis: "Fáze", polozky: SLOUPCE.map((c) => ({ key: c.klic, label: c.nazev, pocet: vSloupci(c).length, ton: c.ton })) },
          { nadpis: "Ostatní", polozky: [{ key: MIMO.klic, label: MIMO.nazev, pocet: vSloupci(MIMO).length, ton: MIMO.ton, popis: "Odložené, ztracené, nedoručené, nekontaktovat" }] },
        ]} />
    </>
  );
}

/* --- Stránka ------------------------------------------------------------------ */
export default function Firmy() {
  const uzka = useUzkaObrazovka();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [firmy, setFirmy] = useState(null);
  const [fronta, setFronta] = useState(null);
  const [obchodnici, setObchodnici] = useState([]);
  const [chyba, setChyba] = useState("");
  const [rezim, setRezim] = useState("tabulka");
  const [q, setQ] = useState("");
  const [qHledej, setQHledej] = useState("");
  const [stav, setStav] = useState("");
  const [seg, setSeg] = useState("");
  const [obch, setObch] = useState("");
  const [nova, setNova] = useState(params.get("nova") === "1");

  useEffect(() => { const t = setTimeout(() => setQHledej(q), 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => { b2bGet("obchodnici").then((r) => setObchodnici(r.obchodnici || [])).catch(() => {}); }, []);

  const nacti = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([
        b2bGet("firmy", { q: qHledej, stav, segment: seg, obchodnik: obch }),
        b2bGet("dnes"),
      ]);
      setFirmy(a.firmy); setFronta(b); setChyba("");
    } catch (e) { setChyba(e.message); }
  }, [qHledej, stav, seg, obch]);
  useEffect(() => { nacti(); }, [nacti]);

  const zavritNovou = () => { setNova(false); if (params.get("nova")) { params.delete("nova"); setParams(params, { replace: true }); } };
  const obchVolby = useMemo(() => [{ key: "", label: "Všichni" }, { key: "__nikdo", label: "Bez obchodníka" }, ...obchodnici.map((o) => ({ key: o.email, label: o.jmeno }))], [obchodnici]);
  const akceHlavicky = [
    { klic: "firma", ikona: Plus, popis: "Nová firma", onClick: () => setNova(true) },
    { klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: nacti },
  ];
  const filtruje = !!(qHledej || stav || seg || obch);

  return (
    <Stranka sirka={rezim === "kanban" && !uzka ? "plna" : "bezna"}>
      <GradientHeader title="Firmy" icon={Building2} akce={akceHlavicky} />
      {chyba && <ErrorNote onRetry={nacti}>{chyba}</ErrorNote>}
      {!filtruje && <FrontaDnes data={fronta} uzka={uzka} />}
      <PruhFiltru>
        <div className="grid gap-3">
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,230px)]">
            <div className="relative min-w-0">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"><Search className="h-4 w-4" /></span>
              <input className="field !min-h-[56px] !rounded-2xl !pl-9" placeholder="Hledat firmu, IČO nebo osobu" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Hledat firmu" />
            </div>
            <Segment items={REZIMY} value={rezim} onChange={setRezim} className="self-stretch" aria-label="Tabulka nebo kanban" />
          </div>
          <div className={"grid gap-3 " + (uzka ? "" : "lg:grid-cols-3")}>
            <RozbalovaciVyber stitek="Stav" items={volbyStavu()} value={stav} onChange={setStav} aria-label="Stav" />
            <RozbalovaciVyber stitek="Segment" items={volbySegmentu()} value={seg} onChange={setSeg} aria-label="Segment" />
            {!uzka && <RozbalovaciVyber stitek="Obchodník" items={obchVolby} value={obch} onChange={setObch} aria-label="Obchodník" />}
          </div>
        </div>
      </PruhFiltru>
      {!firmy && !chyba && <SkeletonRows rows={5} />}
      {firmy && firmy.length === 0 && (
        <EmptyState icon={Building2} title={filtruje ? "Nic nenalezeno" : "Zatím tu není žádná firma"}
          hint={filtruje ? "Zkus změnit hledání nebo filtry." : "Založ první firmu tlačítkem plus nahoře."} />
      )}
      {firmy && firmy.length > 0 && (rezim === "kanban"
        ? <Kanban firmy={firmy} uzka={uzka} />
        : uzka ? <SeznamFirem firmy={firmy} /> : <TabulkaFirem firmy={firmy} obchodnici={obchodnici} />)}
      <FirmaDialog open={nova} onClose={zavritNovou} onHotovo={(f) => { zavritNovou(); navigate(`/b2b/firma/${f.id}`); }} />
    </Stranka>
  );
}
